import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  inject,
  model,
  output,
  type ForwardRefFn,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlContainer,
  NG_VALUE_ACCESSOR,
  type ControlValueAccessor,
} from '@angular/forms';
import { debounceTime, tap } from 'rxjs/operators';

import { MYUI_COMPONENT_SELECTOR, MyuiComponent } from './abstract.component';
import { MYUI_PREFIX } from './abstract.constant';
import { type MyuiEvent } from './abstract.interface';

export type MyuiEditableType<V = unknown> = V | null;

export type MyuiEditableTouchedFn = () => void;

export type MyuiEditableChangeFn<V = unknown> = (
  _: MyuiEditableType<V>,
) => void;

export const MYUI_EDITABLE_SELECTOR = `${MYUI_PREFIX}-editable`;

export const MYUI_FORM_CONTAINER = {
  provide: ControlContainer,
  useFactory: () =>
    inject(ControlContainer, { skipSelf: true, optional: true }),
};
export function MYUI_FORM_CONTAINER_PROVIDER(fn: ForwardRefFn) {
  return {
    multi: true,
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(fn),
  };
}

@Component({
  template: '',
  selector: MYUI_EDITABLE_SELECTOR,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MYUI_FORM_CONTAINER_PROVIDER(() => MyuiEditable)],
})
export abstract class MyuiEditable<V = unknown>
  extends MyuiComponent
  implements ControlValueAccessor
{
  public override mSelector = MYUI_EDITABLE_SELECTOR;
  public override readonly mComponentClasses = computed(() =>
    [MYUI_COMPONENT_SELECTOR, MYUI_EDITABLE_SELECTOR].join(' '),
  );

  public abstract readonly mContainer: ControlContainer | null;

  public readonly disabled = model<boolean>(false);
  public readonly readonly = model<boolean>(false);

  public readonly value = model<MyuiEditableType<V>>(null);

  public readonly errors = model<Record<string, boolean>>();
  public readonly showErrors = model<boolean>(false);

  public readonly changed = output<MyuiEvent<MyuiEditableType<V>>>();

  protected _onModelTouched: MyuiEditableTouchedFn = () => {};

  protected _onModelChange: MyuiEditableChangeFn<V> = (
    _: MyuiEditableType<V>,
  ) => {};

  constructor() {
    super();
    this._initializeListeners();
  }

  /**
   * This is triggered when a value is updated from within the component.
   */
  public abstract setValue(value: MyuiEditableType<V>): void;

  /**
   * This is triggered when a value is set externally to the component.
   */
  public abstract updateValue(value: MyuiEditableType<V>): void;

  /**
   * Resets the value of the editable component to null.
   */
  public resetValue(): void {
    this.setValue(null);
  }

  // Writers
  public writeValue(value: MyuiEditableType<V>): void {
    this.updateValue(value);
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // Registers
  public registerOnChange(fn: MyuiEditableChangeFn): void {
    this._onModelChange = fn;
  }

  public registerOnTouched(fn: MyuiEditableTouchedFn): void {
    this._onModelTouched = fn;
  }

  // Listeners
  protected _initializeListeners(): void {
    if (this.mContainer) {
      this.mContainer.statusChanges
        .pipe(
          takeUntilDestroyed(),
          debounceTime(100),
          tap(() => {
            this.errors.set(this.mContainer.control.errors);
          }),
        )
        .subscribe();
    }
  }
}
